import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
    Button,
    Chip,
    CircularProgress,
    Container,
    Pagination,
    PaginationItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';
import _ from 'lodash';

import type { ActivationWithService } from '../hooks/activations';
import { useActivations } from '../hooks/activations';

import { RouteTo } from '../routes';

import styles from './Activations.module.scss';

/**
 * Color mapping for each activation type.
 */
const ACTIVATION_TYPE_COLORS: Record<string, string> = {
    pack: '#6C63FF',
    banner: '#FF6B6B',
    portal_post: '#2F77FF',
    portal_featured_post: '#00B4D8',
    gift: '#20882E',
    legacy_gift: '#8BC34A',
    event: '#FF9800',
};

const DEFAULT_COLOR = '#818181';

function getColorForType(type: string) {
    return ACTIVATION_TYPE_COLORS[type] ?? DEFAULT_COLOR;
}

function getTypeLabel(type: string): string {
    const labels: Record<string, string> = {
        pack: 'Pack',
        banner: 'Banner',
        portal_post: 'Portal Post',
        portal_featured_post: 'Featured Post',
        gift: 'Gift',
        legacy_gift: 'Legacy Gift',
        event: 'Event',
    };
    return labels[type] ?? type;
}

/** Get the CMS route for a given activation, or undefined if no page exists */
function getActivationRoute(
    type: string,
    id: string | number,
): string | undefined {
    const idStr = String(id);
    switch (type) {
        case 'pack':
            return RouteTo.PackEdit(idStr);
        case 'banner':
            return RouteTo.BannerEdit(idStr);
        case 'portal_post':
        case 'portal_featured_post':
            return RouteTo.PortalPostEdit(idStr);
        case 'legacy_gift':
            return RouteTo.GiftEdit(idStr);
        case 'gift':
            return RouteTo.GiftsV2();
        default:
            return undefined;
    }
}

const MONTH_INDICES = _.range(0, 12);
const DATE_TIME_FORMAT = new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
});

interface CalendarEvent {
    activation: ActivationWithService;
    kind: 'activation' | 'deactivation';
    date: Date;
}

/** Build calendar events: one "activation" per start date, one "deactivation" per end date */
function buildCalendarEvents(
    activations: ActivationWithService[],
): CalendarEvent[] {
    const events: CalendarEvent[] = [];
    for (const activation of activations) {
        events.push({
            activation,
            kind: 'activation',
            date: new Date(activation.startDate),
        });
        if (activation.endDate) {
            events.push({
                activation,
                kind: 'deactivation',
                date: new Date(activation.endDate),
            });
        }
    }
    return events.sort((a, b) => a.date.getTime() - b.date.getTime());
}

const EVENT_COLORS = {
    activation: '#2e7d32',
    deactivation: '#d32f2f',
} as const;

export function Activations() {
    const navigate = useNavigate();
    const { activations, isLoading, error, refetch } = useActivations();
    const [enabledTypes, setEnabledTypes] = useState<Set<string> | null>(null);
    const [enabledKinds, setEnabledKinds] = useState<Set<string>>(
        new Set(['activation', 'deactivation']),
    );
    const [selectedPage, setSelectedPage] = useState<number | null>(null);
    const theme = useTheme();

    // Collect all unique types from the full data set
    const allTypes = useMemo(() => {
        const types = new Set(activations.map((a) => a.type));
        return [...types].sort();
    }, [activations]);

    const activeEnabledTypes = useMemo(
        () => enabledTypes ?? new Set(allTypes),
        [enabledTypes, allTypes],
    );

    const filteredActivations = useMemo(
        () => activations.filter((a) => activeEnabledTypes.has(a.type)),
        [activations, activeEnabledTypes],
    );

    const toggleType = (type: string) => {
        setEnabledTypes((prev) => {
            const current = prev ?? new Set(allTypes);
            const allEnabled = allTypes.every((t) => current.has(t));
            if (allEnabled) {
                return new Set([type]);
            }
            const next = new Set(current);
            if (next.has(type)) {
                next.delete(type);
            } else {
                next.add(type);
            }
            return next;
        });
    };

    const toggleAll = () => {
        setEnabledTypes((prev) => {
            const current = prev ?? new Set(allTypes);
            const allEnabled = allTypes.every((t) => current.has(t));
            return allEnabled ? new Set<string>() : new Set(allTypes);
        });
    };

    const allCalendarEvents = useMemo(
        () => buildCalendarEvents(filteredActivations),
        [filteredActivations],
    );

    const calendarEvents = useMemo(
        () => allCalendarEvents.filter((e) => enabledKinds.has(e.kind)),
        [allCalendarEvents, enabledKinds],
    );

    const years = useMemo(
        () =>
            _.sortedUniq(
                calendarEvents
                    .map((e) => e.date.getFullYear())
                    .toSorted((a, b) => a - b),
            ),
        [calendarEvents],
    );

    const groupedByYear = useMemo(
        () => _.groupBy(calendarEvents, (e) => e.date.getFullYear()),
        [calendarEvents],
    );

    const currentYearPage = useMemo(() => {
        const idx = years.indexOf(new Date().getFullYear());
        return idx !== -1 ? idx + 1 : 1;
    }, [years]);

    const effectivePage = selectedPage ?? currentYearPage;

    const groupedByMonth = useMemo(() => {
        const year = years[effectivePage - 1];
        const yearEvents = groupedByYear[year] ?? [];
        return _.groupBy(yearEvents, (e) => e.date.getMonth());
    }, [groupedByYear, effectivePage, years]);

    const allEnabled = allTypes.every((t) => activeEnabledTypes.has(t));

    if (isLoading) {
        return (
            <Container>
                <Stack sx={{ alignItems: 'center', gap: 2, my: 4 }}>
                    <CircularProgress />
                    <Typography>Loading activations…</Typography>
                </Stack>
            </Container>
        );
    }

    if (error) {
        return (
            <Container>
                <Stack sx={{ alignItems: 'center', gap: 2, my: 4 }}>
                    <Typography color="error">
                        Failed to load activations.
                    </Typography>
                    <Chip label="Retry" onClick={() => refetch()} />
                </Stack>
            </Container>
        );
    }

    return (
        <Container>
            <Stack sx={{ gap: 2, my: 2 }}>
                <div className={styles.header}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Activations
                    </Typography>
                    <div className={styles.filters}>
                        <Chip
                            label="All"
                            size="small"
                            variant={allEnabled ? 'filled' : 'outlined'}
                            onClick={toggleAll}
                            sx={{
                                borderColor: '#666',
                                ...(allEnabled
                                    ? {
                                          backgroundColor: '#666',
                                          color: '#fff',
                                          '&:hover': {
                                              backgroundColor: '#555',
                                          },
                                      }
                                    : {}),
                            }}
                        />
                        {allTypes.map((type) => {
                            const color = getColorForType(type);
                            const isActive = activeEnabledTypes.has(type);
                            return (
                                <Chip
                                    key={type}
                                    label={getTypeLabel(type)}
                                    size="small"
                                    variant={isActive ? 'filled' : 'outlined'}
                                    onClick={() => toggleType(type)}
                                    sx={{
                                        borderColor: color,
                                        ...(isActive
                                            ? {
                                                  backgroundColor: color,
                                                  color: '#fff',
                                                  '&:hover': {
                                                      backgroundColor: color,
                                                      opacity: 0.85,
                                                  },
                                              }
                                            : {}),
                                    }}
                                />
                            );
                        })}
                    </div>
                    <div className={styles.filters}>
                        <Chip
                            label="Activations"
                            size="small"
                            variant={
                                enabledKinds.has('activation')
                                    ? 'filled'
                                    : 'outlined'
                            }
                            onClick={() =>
                                setEnabledKinds((prev) => {
                                    const next = new Set(prev);
                                    if (next.has('activation')) {
                                        next.delete('activation');
                                    } else {
                                        next.add('activation');
                                    }
                                    return next;
                                })
                            }
                            sx={{
                                borderColor: EVENT_COLORS.activation,
                                ...(enabledKinds.has('activation')
                                    ? {
                                          backgroundColor:
                                              EVENT_COLORS.activation,
                                          color: '#fff',
                                          '&:hover': {
                                              backgroundColor:
                                                  EVENT_COLORS.activation,
                                              opacity: 0.85,
                                          },
                                      }
                                    : {}),
                            }}
                        />
                        <Chip
                            label="De-activations"
                            size="small"
                            variant={
                                enabledKinds.has('deactivation')
                                    ? 'filled'
                                    : 'outlined'
                            }
                            onClick={() =>
                                setEnabledKinds((prev) => {
                                    const next = new Set(prev);
                                    if (next.has('deactivation')) {
                                        next.delete('deactivation');
                                    } else {
                                        next.add('deactivation');
                                    }
                                    return next;
                                })
                            }
                            sx={{
                                borderColor: EVENT_COLORS.deactivation,
                                ...(enabledKinds.has('deactivation')
                                    ? {
                                          backgroundColor:
                                              EVENT_COLORS.deactivation,
                                          color: '#fff',
                                          '&:hover': {
                                              backgroundColor:
                                                  EVENT_COLORS.deactivation,
                                              opacity: 0.85,
                                          },
                                      }
                                    : {}),
                            }}
                        />
                    </div>
                </div>

                <Paper>
                    <TableContainer>
                        <Stack
                            direction="row"
                            sx={{ alignItems: 'center', gap: 1, my: 1, ml: 1 }}
                        >
                            <Pagination
                                page={effectivePage}
                                onChange={(_, page) => setSelectedPage(page)}
                                count={years.length}
                                renderItem={(item) => (
                                    <PaginationItem
                                        {...item}
                                        page={
                                            item.page
                                                ? years[item.page - 1]
                                                : item.page
                                        }
                                    />
                                )}
                            />
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setSelectedPage(currentYearPage)}
                            >
                                Go To {new Date().getFullYear()}
                            </Button>
                        </Stack>
                        <Table size="small" className="no-highlight">
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            background: theme.palette.grey.A700,
                                            color: theme.palette.common.white,
                                        }}
                                    >
                                        Event
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            background: theme.palette.grey.A700,
                                            color: theme.palette.common.white,
                                        }}
                                    >
                                        Type
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            background: theme.palette.grey.A700,
                                            color: theme.palette.common.white,
                                        }}
                                    >
                                        ID
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            background: theme.palette.grey.A700,
                                            color: theme.palette.common.white,
                                        }}
                                    >
                                        Service
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            background: theme.palette.grey.A700,
                                            color: theme.palette.common.white,
                                        }}
                                    >
                                        Date
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {MONTH_INDICES.map((month) => {
                                    const monthEvents =
                                        groupedByMonth[month] ?? [];
                                    const selectedYear =
                                        years[effectivePage - 1] ??
                                        new Date().getFullYear();
                                    return (
                                        <React.Fragment key={month}>
                                            <TableRow>
                                                <TableCell
                                                    colSpan={5}
                                                    sx={{
                                                        background:
                                                            theme.palette.grey
                                                                .A700,
                                                        color: theme.palette
                                                            .common.white,
                                                        py: 1.5,
                                                        fontWeight: 'bold',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {new Date(
                                                        selectedYear,
                                                        month,
                                                    ).toLocaleString(
                                                        'default',
                                                        {
                                                            year: 'numeric',
                                                            month: 'long',
                                                        },
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            {monthEvents.length === 0 && (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={5}
                                                        sx={{
                                                            textAlign: 'center',
                                                            color: theme.palette
                                                                .text.secondary,
                                                            py: 1,
                                                        }}
                                                    >
                                                        No events
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            {monthEvents.map((event, index) => (
                                                <EventRow
                                                    key={`${event.kind}-${event.activation.service}-${event.activation.type}-${event.activation.id}-${index}`}
                                                    event={event}
                                                    onClick={() => {
                                                        const route =
                                                            getActivationRoute(
                                                                event.activation
                                                                    .type,
                                                                event.activation
                                                                    .id,
                                                            );
                                                        if (route)
                                                            navigate(route);
                                                    }}
                                                />
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Stack>
        </Container>
    );
}

interface EventRowProps {
    event: CalendarEvent;
    onClick: () => void;
}

const EventRow = ({ event, onClick }: EventRowProps) => {
    const { activation, kind, date } = event;
    const typeColor = getColorForType(activation.type);
    const route = getActivationRoute(activation.type, activation.id);
    const eventColor = EVENT_COLORS[kind];

    return (
        <TableRow
            hover
            onClick={onClick}
            sx={{ cursor: route ? 'pointer' : 'default' }}
        >
            <TableCell>
                <Chip
                    label={
                        kind === 'activation' ? 'Activation' : 'De-activation'
                    }
                    size="small"
                    sx={{
                        backgroundColor: eventColor,
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: 11,
                    }}
                />
            </TableCell>
            <TableCell>
                <Chip
                    label={getTypeLabel(activation.type)}
                    size="small"
                    sx={{
                        backgroundColor: typeColor,
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: 11,
                    }}
                />
            </TableCell>
            <TableCell>{activation.id}</TableCell>
            <TableCell sx={{ textTransform: 'capitalize' }}>
                {activation.service}
            </TableCell>
            <TableCell>
                {DATE_TIME_FORMAT.format(date)} (Swedish Time)
            </TableCell>
        </TableRow>
    );
};
